/**
 * Nexus Debug Mode
 *
 * Activates hidden debug tools in production without cluttering the UI.
 *
 * Shortcut: hold Alt+Shift on every platform, type N → X → S in sequence.
 * Each key must follow the previous within 2 seconds.
 *
 * Alt, not Cmd or Ctrl: those chords open a private window on macOS and collide
 * with browser shortcuts on Windows. Do not "restore" either one.
 *
 * On trigger: password modal opens. Enter NEXUS_UI_BACKDOOR to unlock.
 * Session-scoped: clears when the browser tab closes (sessionStorage).
 *
 * Completely independent from DevModeProvider — different purpose, different users.
 */

import {
  NxButton,
  NxDialog,
  NxDialogBody,
  NxDialogClose,
  NxDialogContent,
  NxDialogDescription,
  NxDialogFooter,
  NxDialogHeader,
  NxDialogTitle,
  NxField,
  NxFieldLabel,
  NxInputGroup,
  NxInputGroupInput
} from "@agent-nexus/ui"
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react"

import { getBackendUrl } from "@/config/env"

// =============================================================================
// Context
// =============================================================================

interface DebugModeContextType {
  isDebugMode: boolean
  disableDebugMode: () => void
}

const DebugModeContext = createContext<DebugModeContextType>({
  isDebugMode: false,
  disableDebugMode: () => undefined
})

const SESSION_KEY = "nexus_debug_unlocked"
// Use e.code (layout-independent) — e.key with Alt on Mac produces special Unicode chars
const SEQUENCE_CODES = ["KeyN", "KeyX", "KeyS"] as const
const SEQUENCE_TIMEOUT_MS = 2000

// =============================================================================
// Provider
// =============================================================================

export function DebugModeProvider({ children }: PropsWithChildren) {
  const [isDebugMode, setIsDebugMode] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) === "1"
    } catch {
      return false
    }
  })

  const [showModal, setShowModal] = useState(false)
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)

  // Track the NXS key sequence
  const sequenceRef = useRef<number>(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const resetSequence = useCallback(() => {
    sequenceRef.current = 0
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
  }, [])

  const disableDebugMode = useCallback(() => {
    setIsDebugMode(false)
    try {
      sessionStorage.removeItem(SESSION_KEY)
    } catch {
      // ignore
    }
  }, [])

  // Keyboard shortcut: Alt+Shift + N → X → S, every platform
  useEffect(() => {
    const scheduleReset = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      timeoutRef.current = setTimeout(resetSequence, SEQUENCE_TIMEOUT_MS)
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip modifier key presses themselves — they would reset the sequence
      if (["Alt", "Shift", "Meta", "Control"].includes(e.key)) return

      // Require Alt+Shift held during the sequence
      if (!e.altKey || !e.shiftKey) {
        resetSequence()
        return
      }

      // Use e.code for layout-independent detection
      // e.key with Alt/Option on Mac produces special Unicode chars (e.g. Option+N = 'ŋ')
      const code = e.code
      const expected = SEQUENCE_CODES[sequenceRef.current]

      if (code !== expected) {
        resetSequence()
        // Re-check if this code starts the sequence
        if (code === SEQUENCE_CODES[0]) {
          sequenceRef.current = 1
          scheduleReset()
        }
        return
      }

      e.preventDefault()
      sequenceRef.current++

      if (sequenceRef.current === SEQUENCE_CODES.length) {
        resetSequence()
        if (isDebugMode) {
          disableDebugMode()
        } else {
          setShowModal(true)
          setPassword("")
          setError(null)
        }
      } else {
        scheduleReset()
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isDebugMode, disableDebugMode, resetSequence])

  const handleVerify = useCallback(async () => {
    if (!password.trim()) return

    setIsVerifying(true)
    setError(null)

    try {
      const res = await fetch(`${getBackendUrl()}/api/debug/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password })
      })

      const data = (await res.json()) as { valid?: boolean }

      if (data.valid) {
        sessionStorage.setItem(SESSION_KEY, "1")
        setIsDebugMode(true)
        setShowModal(false)
        setPassword("")
      } else {
        setError("Invalid password")
        setPassword("")
      }
    } catch {
      setError("Could not reach server")
      setPassword("")
    } finally {
      setIsVerifying(false)
    }
  }, [password])

  return (
    <DebugModeContext.Provider value={{ isDebugMode, disableDebugMode }}>
      {children}
      <NxDialog
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open)
          if (!open) setPassword("")
        }}
      >
        <NxDialogContent size="sm" asChild>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void handleVerify()
            }}
          >
            <NxDialogHeader>
              <NxDialogTitle>Debug Mode</NxDialogTitle>
            </NxDialogHeader>
            <NxDialogBody>
              <NxDialogDescription>
                Enter the debug password to unlock debug tooling for this session.
              </NxDialogDescription>
              <div className="space-y-3">
                <NxField>
                  <NxFieldLabel>Password</NxFieldLabel>
                  <NxInputGroup>
                    <NxInputGroupInput
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value)
                        setError(null)
                      }}
                      placeholder="Enter debug password"
                    />
                  </NxInputGroup>
                </NxField>
                {error && (
                  <p role="alert" className="text-nx-text-error text-xs">
                    {error}
                  </p>
                )}
                <p className="text-nx-text-tertiary text-xs">
                  Alt+Shift+N→X→S to toggle · Session only
                </p>
              </div>
            </NxDialogBody>
            <NxDialogFooter>
              <NxDialogClose asChild>
                <NxButton variant="secondary" size="default" type="button">
                  Cancel
                </NxButton>
              </NxDialogClose>
              <NxButton
                variant="default"
                size="default"
                type="submit"
                isLoading={isVerifying}
                disabled={!password.trim()}
              >
                Unlock
              </NxButton>
            </NxDialogFooter>
          </form>
        </NxDialogContent>
      </NxDialog>
    </DebugModeContext.Provider>
  )
}

// =============================================================================
// Hook
// =============================================================================

export function useDebugMode(): DebugModeContextType {
  return useContext(DebugModeContext)
}
